import React, { useRef } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import Flag, {
	AutoDismissFlag,
	type CreateFlagArgs,
	type FlagAPI,
	useFlags as useAKFlags,
} from '@atlaskit/flag';
import { assert, AssertionError } from '@atlassian/eoc-common';

import { type FlagComponent, type FlagService } from '../../common/utils/types';
import { useExternalFlagService } from '../index';

const useFlags = (): FlagAPI => {
	const [flagService] = useExternalFlagService();
	const serviceRef = useRef(flagService);

	assert(
		!serviceRef.current || !!flagService,
		new AssertionError('GlobalContext', 'flagService was destroyed.'),
	);

	return serviceRef.current !== undefined
		? {
				showFlag: ({ isAutoDismiss, ...props }: CreateFlagArgs) => {
					const Component = ({ id, onDismissed }: FlagComponent) => {
						if (isAutoDismiss) {
							return (
								<AutoDismissFlag
									{...props}
									id={id}
									onDismissed={(id, e) => {
										onDismissed(id.toString());
										props.onDismissed?.(id, e);
									}}
								/>
							);
						}
						return (
							<Flag
								{...props}
								id={id}
								onDismissed={(id, e) => {
									onDismissed(id.toString());
									props.onDismissed?.(id, e);
								}}
							/>
						);
					};
					// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
					const id = uuidv4() as string;
					Component.id = id;
					(serviceRef.current as FlagService).showFlag(Component);
					return () => {
						(serviceRef.current as FlagService).dismissFlag({
							id,
							command: 'DISMISS',
						});
					};
				},
			}
		: // We are sure that we either call this hook always or not.
			//eslint-disable-next-line react-hooks/rules-of-hooks
			useAKFlags();
};

export default useFlags;
